<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      :image-url="referente?.photo_url || ''"
      :alt="referente?.name || 'Referente histórico'"
      :title="referente?.name || 'Referente histórico'"
      :subtitle="referente?.association || ''"
    />

    <section
      v-if="loading"
      class="px-6 py-20 text-center text-bochas-text-muted"
    >
      Cargando jugador...
    </section>

    <!-- Detalle -->
    <RevealOnScroll
      as="article"
      v-else-if="referente"
      class="px-6 lg:px-0 py-12 lg:pt-24 lg:pb-0 max-w-7xl mx-auto"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="pasado" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Pasado</span>
      </div>

      <!-- Info meta -->
      <div
        v-if="
          referente.association ||
          referente.birthDate ||
          referente.deathDate ||
          referente.careerStartDate ||
          referente.careerEndDate
        "
        class="space-y-2 mb-6"
      >
        <p
          v-if="referente.association"
          class="flex items-center gap-2 text-base sm:text-lg text-white/80"
        >
          <IconFbochas name="location" :size="14" color="white" />
          <span>{{ referente.association }}</span>
        </p>
        <p
          v-if="referente.birthDate || referente.deathDate"
          class="flex items-center gap-2 text-base sm:text-lg text-white/80"
        >
          <IconFbochas name="reloj" :size="14" color="white" />
          <span>
          <template v-if="referente.birthDate">{{ dateYear(referente.birthDate) }}</template>
          <template v-if="referente.birthDate && referente.deathDate"> - </template>
          <template v-if="referente.deathDate">{{ dateYear(referente.deathDate) }}</template>
          </span>
        </p>
        <p
          v-if="referente.careerStartDate || referente.careerEndDate"
          class="flex items-center gap-2 text-base sm:text-lg text-white/80"
        >
          <span class="text-bochas-rojo font-semibold">Período:</span>
          <span>{{ careerPeriod(referente) }}</span>
        </p>
      </div>

      <PlayerContactLinks :player="referente" class="mb-6 sm:mb-8" />

      <!-- Imagen principal -->
      <div
        class="detail-motion-media h-[450px] overflow-hidden rounded-xl mb-8 bg-bochas-bg"
      >
        <img
          v-if="referente.photo_url"
          :src="referente.photo_url"
          :alt="referente.name"
          class="w-full h-full object-cover"
          loading="lazy"
        />
        <div
          v-else
          class="flex h-full items-center justify-center bg-degradado-bochas text-6xl font-bold text-white"
        >
          {{ initials }}
        </div>
      </div>

      <!-- Descripción -->
      <RichTextContent
        v-if="referente.bio"
        :html="referente.bio"
        class="text-bochas-text-muted text-sm sm:text-base leading-relaxed mb-10"
      />

      <!-- Logros -->
      <div
        v-if="referente.achievements?.length"
        class="listing-motion-card card-fbochas p-6 sm:p-8 mb-10"
      >
        <div class="flex items-center gap-3 mb-4">
          <IconFbochas name="trophy" :size="26" color="#a52121" />
          <h3 class="text-xl sm:text-2xl font-bold text-white">Logros</h3>
        </div>
        <ul class="space-y-2">
          <li
            v-for="(logro, idx) in referente.achievements"
            :key="idx"
            class="text-white flex items-start gap-2"
          >
            <span class="text-bochas-rojo mt-1">•</span>
            <span>{{ logro }}</span>
          </li>
        </ul>
      </div>

      <!-- Volver -->
      <div class="py-24">
        <VolverButton to="/pasado" />
      </div>
    </RevealOnScroll>

    <section v-else class="px-6 lg:px-0 py-20 max-w-7xl mx-auto text-center">
      <p class="text-bochas-text-muted text-base sm:text-lg">
        Referente no encontrado.
      </p>
      <div class="mt-4">
        <VolverButton to="/pasado" text="Volver a jugadores históricos" />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { obtenerReferente } from "@/services/referentes/referentesService";
import { careerPeriod } from "@/utils/playerCareer";
import PlayerContactLinks from "@/components/Common/PlayerContactLinks.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import { applyContentSeo, applyNotFoundSeo } from "@/services/seo/seoService";

const route = useRoute();
const id = computed(() => route.params.slug);
const referente = ref(null);
const loading = ref(true);
function dateYear(value) {
  return String(value || "").split("-")[0];
}
const initials = computed(() =>
  String(referente.value?.name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase(),
);
async function load() {
  loading.value = true;
  try {
    referente.value = await obtenerReferente(id.value);
    await applyContentSeo({
      title: referente.value.name,
      description: referente.value.bio,
      image: referente.value.photo_url,
    });
  } catch (_) {
    referente.value = null;
    applyNotFoundSeo();
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(id, load);
</script>
